import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { faGraduationCap, faKey } from '@fortawesome/pro-solid-svg-icons'

import { isObject, recursiveIncludes, validateFormInput } from '../../utils'
import { ROLE_ADMIN_HOSPITAL_RO, ROLE_ADMIN_RESEAU, ROLE_LOCAL_SECRETARY, ROLE_INTERNSHIP_MASTER_MACCS, ROLE_PRESI_MASTER, ROLE_CLINICAL_SECRETARY, ROLE_INSTITUTION_GROUP_ADMIN } from '../../utils/constants'
import SupervisedUserSpecialities from '../MACCS/Settings/SupervisedUserSpecialities'
import AdminResetPassword from './AdminResetPassword'
import SmartTable, { SmartTableDataTypes } from './SmartTable'
import { GlobalContext } from '../../Providers/GlobalProvider'
import { getAllUsers } from '../../utils/api/users'
import { onConsoleError, onError, onSuccess } from '../../utils/apiHelper'
import { isAdminHospitalRO, isClinicalSecretary, isInstitutionGroupAdmin, isInstitutionMaccsUser, isInternshipMasterMaccs, isPresiMaster, isSchoolMaccsUser } from '../../utils/roles'
import { selectLanguages, selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { createUser, deleteUser, updateUser } from '../../utils/api/user'
import { HttpStatuses } from '../../utils/http'
import UserSpecialities from '../../Views/MACCS/UsersView/UserSpecialities'

const MaccsUsers = () => {
  const { user, permissions } = useContext(GlobalContext)

  const t = useSelector(selectTranslate)
  const languages = useSelector(selectLanguages)

  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedPasswordResettingUser, setSelectedPasswordResettingUser] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)

  useEffect(() => {
    if (user) {
      setLoading(true)
      getAllUsers(user, { removePagination: true, contexts: ['stats'] }).then(({ data }) => {
        setUsers(data.map(u => {
          if (u.roles) {
            u.roles = u.roles[0]
          }

          if (u.lastLogin) {
            u.lastLogin = u.lastLogin.split('+')[0]
          }

          return u
        }))
      }).catch(error => {
        onConsoleError(error, t('users.get_all.error'))
      }).finally(() => {
        setLoading(false)
      })
    }
  }, [user])

  const roles = useMemo(() => {
    let roles = []

    if (isSchoolMaccsUser(user)) {
      roles = [
        { id: ROLE_ADMIN_RESEAU, name: t('roles.' + ROLE_ADMIN_RESEAU.toLowerCase()) },
        { id: ROLE_PRESI_MASTER, name: t('roles.' + ROLE_PRESI_MASTER.toLowerCase()) },
        { id: ROLE_CLINICAL_SECRETARY, name: t('roles.' + ROLE_CLINICAL_SECRETARY.toLowerCase()) }
      ]
    } else if (isInstitutionMaccsUser(user)) {
      roles = [
        { id: ROLE_LOCAL_SECRETARY, name: t('roles.' + ROLE_LOCAL_SECRETARY.toLowerCase()) },
        { id: ROLE_INTERNSHIP_MASTER_MACCS, name: t('roles.' + ROLE_INTERNSHIP_MASTER_MACCS.toLowerCase()) },
        { id: ROLE_ADMIN_HOSPITAL_RO, name: t('roles.' + ROLE_ADMIN_HOSPITAL_RO.toLowerCase()) }
      ]
    }

    return roles
  }, [user.roles, t])

  const localLanguages = useMemo(() => languages.map(l => ({ id: l.code, name: t(l.name) })), [languages])
  const isDisabled = useCallback(u => {
    const readOnlyMode = isAdminHospitalRO(user) || isInternshipMasterMaccs(user) || isPresiMaster(user) || isClinicalSecretary(user)
    const dataDisabled = isInstitutionGroupAdmin(user) ? t(ROLE_INSTITUTION_GROUP_ADMIN) : null

    return readOnlyMode || dataDisabled
  }, [user.roles, t])
  const columns = useMemo(() => {
    return [
      { type: SmartTableDataTypes.ID, key: 'id' },
      {
        type: SmartTableDataTypes.STRING,
        name: t('common.email'),
        key: 'email',
        validate: data => validateFormInput('email', data, true),
        disabled: isDisabled
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('common.username'),
        key: 'username',
        validate: data => validateFormInput('email', data, true),
        disabled: isDisabled
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('common.lastname'),
        key: 'lastname',
        validate: data => validateFormInput('name', data, true),
        disabled: isDisabled
      },
      {
        type: SmartTableDataTypes.STRING,
        name: t('common.firstname'),
        key: 'firstname',
        validate: data => validateFormInput('name', data, true),
        disabled: isDisabled
      },
      {
        type: SmartTableDataTypes.SELECT,
        name: t('common.language'),
        key: 'language',
        options: localLanguages,
        preventAutoSelectDefaultValue: true,
        validate: data => {
          return data !== null
        },
        disabled: isDisabled
      },
      {
        type: SmartTableDataTypes.SELECT,
        name: t('common.role'),
        key: 'roles',
        options: roles,
        disabled: isDisabled
      },
      {
        type: SmartTableDataTypes.DATE,
        name: t('common.last_login'),
        key: 'lastLogin',
        format: 'DD/MM/YYYY',
        disabled: true
      }
    ]
  }, [isDisabled, roles, localLanguages, t])

  const additionalActions = useMemo(() => {
    const additionalActions = [
      {
        key: 'assigned-specialities',
        iconName: faGraduationCap,
        type: 'primary',
        title: t('Assigned specialities'),
        onClick: setSelectedUser,
        disabledCallback: u => {
          if (recursiveIncludes(
            user.roles,
            [ROLE_PRESI_MASTER, ROLE_INTERNSHIP_MASTER_MACCS, ROLE_CLINICAL_SECRETARY]
          )) {
            return u.id !== user.id
          }

          return recursiveIncludes(
            u.roles,
            [ROLE_ADMIN_RESEAU, ROLE_ADMIN_HOSPITAL_RO, ROLE_LOCAL_SECRETARY, ROLE_INSTITUTION_GROUP_ADMIN]
          )
        }
      }
    ]

    if (permissions.canResetPassword) {
      additionalActions.push({
        key: 'reset-password',
        iconName: faKey,
        type: 'primary',
        title: t('Reset password'),
        onClick: setSelectedPasswordResettingUser
      })
    }

    return additionalActions
  }, [user, permissions.canResetPassword, t, setSelectedPasswordResettingUser, setSelectedUser])

  const handleUserCreation = (newUser, stopTableLoading, reviveUser = false) => {
    if (newUser.email && newUser.username) {
      const userData = { ...newUser, roles: [newUser.roles] }

      userData.reviveDeletedUser = true

      createUser(user, userData).then(({ data }) => {
        setUsers(previous => [...previous, { ...data, roles: data.roles[0] }])
        onSuccess(t('The user was successfully created'))
      }).catch(error => {
        if (error.status === HttpStatuses.CONFLICT) {
          onError(t('users.create.error_already_exists'))
        } else {
          onConsoleError(error, t('users.create.error'))
        }
      }).finally(() => {
        stopTableLoading()
      })
    } else {
      stopTableLoading()
    }
  }

  const handleUserModification = (updatedUser, stopTableLoading, savingValidator = false) => {
    const userData = {
      id: updatedUser.id,
      email: updatedUser.email,
      username: updatedUser.username,
      firstname: updatedUser.firstname,
      lastname: updatedUser.lastname,
      language: updatedUser.language,
      roles: [updatedUser.roles],
      school: isObject(updatedUser.school) ? updatedUser.school.id : null
    }

    updateUser(user, userData).then(({ data }) => {
      setUsers(previous => previous.map(u => {
        if (u.id === updatedUser.id) {
          u = updatedUser
        }

        return u
      }))
      onSuccess(t('The user was successfully updated'))
    }).catch(error => {
      onConsoleError(error, t('users.update.error'))
    }).finally(() => {
      stopTableLoading()
    })
  }

  const handleUserSuppression = deletedUser => {
    deleteUser(user, deletedUser).then(() => {
      setUsers(previous => previous.filter(u => u.id !== deletedUser.id))
    }).catch(error => {
      onConsoleError(error, t('users.delete.error'))
    })
  }

  return (
    <div>
      {columns.length > 0 && (
        <SmartTable
          columns={columns}
          data={users}
          loading={loading}
          onDataEdit={handleUserModification}
          onDataAdd={handleUserCreation}
          onDataDelete={permissions.canDelete ? handleUserSuppression : undefined}
          addDataText={t('Add user')}
          noDataText={t('No user')}
          additionalActions={additionalActions}
          showAddButtonDisabled={!permissions.canCreate}
        />
      )}
      {isSchoolMaccsUser(user) && (
        <SupervisedUserSpecialities
          onClose={() => setSelectedUser(null)}
          targetUser={selectedUser}
        />
      )}
      {isInstitutionMaccsUser(user) && (
        <UserSpecialities selectedUser={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
      {permissions.canResetPassword && (
        <AdminResetPassword
          userToResetPassword={selectedPasswordResettingUser}
          onClose={() => setSelectedPasswordResettingUser(null)}
        />
      )}

    </div>
  )
}

export default MaccsUsers
